/**
 * The fold on the open board (owner, 2026-10-05: "The scroll area with the notes should feel kinda
 * sticky, so it feels like you're overscrolling to get up to it, then it snaps past the resistance,
 * if you don't scroll hard enough it'll stay at up next."). docs/notes.md "The fold".
 *
 * The open board rests with Next up at the top; the notice board lives above it. The fold is the
 * scroll offset that puts what follows the notice board where the board itself starts: the board's
 * height plus the column's gap. Pulling down from the fold reveals the board heavily (it trails the
 * scroll, `boardLag`); on release it either snaps up to the top or back to the fold (`foldSnap`).
 * Below the fold nothing snaps. Both clients (src/board-fold.ts, client/screens/Board.tsx) use these
 * rules and only these; neither keeps a number of its own.
 *
 * Offsets are the scroller's (0 at the top, growing toward the list). Velocities are px per ms of
 * that offset: positive moves toward the list, negative toward the notice board.
 */

/**
 * Revealed this much of the board's height at the moment the finger lifts, it snaps up to the top
 * (owner, 2026-10-05: "There's too little resistance scrolling to notes, a passive residual scroll
 * almost always snaps up to notes"; it was 0.4 and a flick toward the board opened it too).
 */
export const FOLD_REVEAL = 0.5;
/** A release toward the list at least this fast (px per ms) goes to the fold. No flick opens the board. */
export const FOLD_FLICK = 0.5;
/** A drag shorter than this (px) has no direction. */
export const FOLD_DEAD = 8;
/** Within this of a resting place (px), the scroller is there. */
const AT = 1;

/** The fold: the notice board's height and the gap under it, in whole pixels. 0 with no board. */
export function foldOf(boardHeight: number, gap: number): number {
  if (!(boardHeight > 0)) return 0;
  return Math.round(boardHeight + Math.max(0, gap));
}

export interface FoldRelease {
  /** Where the scroller came to rest (or was let go). */
  y: number;
  /** The fold (foldOf). */
  fold: number;
  /** The notice board's own height. */
  boardHeight: number;
  /** How fast it was moving when the finger left (px per ms, + toward the list). */
  velocity?: number;
  /** Where the gesture began. Without it, the drag has no direction. */
  from?: number;
  /** Where the scroller was when the finger lifted; a coast after that never opens the board. */
  released?: number;
  /** The scroller's visible height: a board taller than this can be read down without snapping away. */
  view?: number;
}

/**
 * Where the scroller goes once a gesture is over: 0 (the notice board, in place), the fold (Next up
 * at the top), or null to leave it where it is. Below the fold, and at either resting place, null.
 *
 * - A fast release toward the list goes to the fold. A flick toward the board opens nothing: only
 *   distance does (owner, 2026-10-05: "too little resistance").
 * - A drag toward the list that stops within the board's region goes to the fold (scrolling down
 *   from the top), unless the board is taller than the screen and the fold is not in sight yet,
 *   which is someone reading down the board.
 * - Otherwise, pulled from the fold (or no direction): the top once at least FOLD_REVEAL of the
 *   board showed where the finger lifted (`released`, else `y`), the fold under that. A coast after
 *   the finger lifted adds nothing.
 *
 * Decide the moment the finger lifts (owner, 2026-10-05: "It also floats undecided for too long
 * while there is residual scroll"); the move cancels any coast. A release below the fold decides
 * nothing here; `foldCoast` catches a coast that then climbs into the board.
 */
export function foldSnap({ y, fold, boardHeight, velocity = 0, from, view, released }: FoldRelease): number | null {
  if (!(fold > 0) || !(boardHeight > 0)) return null;
  if (y >= fold - AT || y <= AT) return null;
  const tallRead = view !== undefined && view > 0 && y + view < fold;
  let to: number;
  if (velocity >= FOLD_FLICK) to = tallRead ? -1 : fold;
  else if (from !== undefined && y > from + FOLD_DEAD) to = tallRead ? -1 : fold;
  else to = fold - (released ?? y) >= FOLD_REVEAL * boardHeight ? 0 : fold;
  return to < 0 ? null : to;
}

/**
 * A coast (the scroller still moving after the finger lifted at or below the fold) that climbs
 * into the board's region goes straight back to the fold, the moment it crosses: the list never
 * drifts up into the notice board on its own. null while the coast is still below the fold, or
 * when the finger lifted on the board (foldSnap decided that already).
 */
export function foldCoast({ y, fold, released }: { y: number; fold: number; released: number }): number | null {
  if (!(fold > 0) || !Number.isFinite(released)) return null;
  if (released < fold - AT) return null;
  return y < fold - AT ? fold : null;
}

/**
 * The lag as stops for a piecewise linear map from the scroll offset to how far the board trails
 * (px, drawn that much higher than it sits, translateY(-lag)). Nothing at the fold and nothing once
 * fully revealed, so the board always ends in place; in between the visible part of the board is
 * half of what has been revealed up to FOLD_REVEAL of it (the resistance), then it catches up.
 * The app feeds these to Animated's interpolate (outputs negated); boardLag reads the same stops.
 */
export function lagStops(fold: number, boardHeight: number): { input: number[]; output: number[] } {
  if (!(fold > 0) || !(boardHeight > 0)) return { input: [0, 1], output: [0, 0] };
  const top = Math.max(0, fold - boardHeight);
  const peak = fold - FOLD_REVEAL * boardHeight;
  return { input: [top, peak, fold], output: [0, (FOLD_REVEAL * boardHeight) / 2, 0] };
}

/** How far the board trails the scroll at offset `y` (px, >= 0), from lagStops. */
export function boardLag(y: number, fold: number, boardHeight: number): number {
  const { input, output } = lagStops(fold, boardHeight);
  if (y <= input[0]! || y >= input[input.length - 1]!) return 0;
  for (let i = 1; i < input.length; i++) {
    const a = input[i - 1]!;
    const b = input[i]!;
    if (y <= b) {
      const t = b > a ? (y - a) / (b - a) : 1;
      return Math.round((output[i - 1]! + (output[i]! - output[i - 1]!) * t) * 100) / 100;
    }
  }
  return 0;
}

/** The least height of the scroller's content so the fold can always be reached: a short list leaves room under it. */
export function foldRoom(view: number, fold: number): number {
  return Math.max(0, Math.round(view + fold));
}

/**
 * Where a freshly mounted open board starts: at the fold, or at the top with the board revealed
 * when a note rings for this person or the tour is about to point at the add slip.
 */
export function foldStart({ fold, ringing = false, tour = false }: { fold: number; ringing?: boolean; tour?: boolean }): number {
  return ringing || tour || !(fold > 0) ? 0 : fold;
}

/**
 * The notice board changed height (a slip opened, a note added, the scroll arrived): where the
 * scroller goes so what the person sees stays put, or null to leave it. Resting at the old fold, it
 * moves to the new one; below it, by the change, so the list does not jump; on the board, or while
 * the person is scrolling (`active`), it is left alone.
 */
export function foldKeep({ y, oldFold, newFold, active = false }: { y: number; oldFold: number; newFold: number; active?: boolean }): number | null {
  if (active || newFold === oldFold) return null;
  if (Math.abs(y - oldFold) <= AT) return newFold;
  if (y > oldFold) return Math.max(0, Math.round(y + (newFold - oldFold)));
  return null;
}

/** The tour card that points at the add slip, which lives above the fold. */
export const FOLD_TOUR_TARGET = "note-add";

/** Whether a tour target ("a|b" for either) is the add slip, so the board shows first. */
export function tourWantsBoard(target: string | null | undefined): boolean {
  return !!target && target.split("|").includes(FOLD_TOUR_TARGET);
}

/**
 * The fold's hint (owner, 2026-10-05: "There should be a little up arrow with a scroll icon at the
 * top", then "In the same line as next up but centered", then "The notice board hint shouldn't
 * flicker away, it should always be visible, it's weird that it flickers and dissapeaes"): shown
 * whenever there is a fold, wherever the scroller is. Closed, a tap on it scrolls to the top (each
 * client's `reveal`); open (foldHintOpen on the web, the hook's own state on the app), its arrow
 * points down and a tap goes back to Next up (`close`). It used to show only within FOLD_DEAD of the
 * fold, which blinked on every scroll.
 */
export function foldHintShows(_y: number, fold: number): boolean {
  return fold > 0;
}

/**
 * Whether the hint reads open (owner, 2026-10-05: "The arrow direction on the note hint should rotate
 * when open, and it should be able to be used to close the notes"): its arrow down and its tap back
 * to Next up. The app reads its own open state; this is the web's, from the scroller. Within
 * FOLD_DEAD of the top it is open and at the fold or past it closed; in between it keeps what it was
 * (`was`) while a pull or a scroll is under way, so the arrow never flickers mid-gesture, and once
 * the scroller rests there (`resting`, a tall board being read) it is open. With no touch (`free`,
 * foldFree) nothing snaps, so it is simply the scroller above the fold's midpoint.
 */
export function foldHintOpen({ y, fold, was, resting = false, free = false }: { y: number; fold: number; was: boolean; resting?: boolean; free?: boolean }): boolean {
  if (!(fold > 0) || !Number.isFinite(y)) return false;
  if (free) return y < fold / 2;
  if (y <= FOLD_DEAD) return true;
  if (y >= fold - AT) return false;
  return resting || was;
}

/**
 * Whether the web's fold is free: a device with no touch (a mouse and a keyboard; the owner, on a
 * desktop pass, 2026-10-05: "Some features and updates also straight aren't working on web desktop").
 * There every wheel roll up from Next up ended back at the fold, so the board could not be reached.
 * Free, the board still starts folded (foldStart) but nothing snaps and nothing lags: the wheel and
 * the keys move freely between the board and the list, like any page, and the hint's tap still
 * scrolls between 0 and the fold. Decided once, when the fold is set up.
 */
export function foldFree({ coarse, touch }: { coarse: boolean; touch: boolean }): boolean {
  return !coarse && !touch;
}

/*
 * The app's fold (client/state/useBoardFold.ts; owner, 2026-10-05, after a day of pull rules on the
 * iPhone and then a button-only fold: "remove the restriction to scroll up", "Remove the rotating
 * arrow"). The notice board is the content's first block above Next up and nothing holds it back:
 * the scroller starts at the fold so Next up is at the top, scrolling up shows the board like any
 * content, and the hint is a shortcut up to it. No inset, no snap, no gesture rule. The web above
 * keeps its pull rule.
 */

/** Whether the app's board mounts on the board (offset 0): a note rings for this person or the tour is about to point at the add slip. */
export function foldStartsOpen({ ringing = false, tour = false }: { ringing?: boolean; tour?: boolean }): boolean {
  return ringing || tour;
}

/**
 * Whether the app's notice board counts as in view at offset `y` (the add button steps aside while it
 * is): in view once the scroller is FOLD_DEAD above the fold, out of view at or past the fold, and
 * between the two whatever it was (`was`), so a rest a point either side of the fold never flickers.
 */
export function foldInView(y: number, fold: number, was: boolean): boolean {
  if (!(fold > 0) || !Number.isFinite(y)) return false;
  if (y <= fold - FOLD_DEAD) return true;
  if (y >= fold) return false;
  return was;
}
